import { useAuth } from "hooks";
import React from "react";
import { useGetDriverDetailQuery } from "store/services/driverApi";
import {
  AccessTime,
  AccountCircle,
  Email,
  Phone,
  HealthAndSafety,
  DirectionsCar,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Divider,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import moment from "moment";

const formatDate = (dateString) => moment(dateString).format("MMMM Do, YYYY");

export const DriverSideProfile = () => {
  const { loggedInUser } = useAuth();
  const driverId = loggedInUser?.typeRef;
  const {
    data: driverData,
    isLoading,
    error,
  } = useGetDriverDetailQuery(driverId);

  if (isLoading) return <InfoGridSkeleton />;
  if (error)
    return (
      <Box sx={{ textAlign: "center", color: "error.main" }}>
        Error loading driver data
      </Box>
    );
  if (!driverData)
    return (
      <Box sx={{ textAlign: "center", color: "text.secondary" }}>
        No driver data found
      </Box>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 3,
        color: "text.primary",
        width: "100%",
      }}
    >
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
          p: 3,
          width: "100%",
        }}
      >
        {/* Driver Details */}
        <Typography variant="h5" color="primary" gutterBottom>
          Your Details
        </Typography>
        <Box mb={4}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <AccountCircle color="primary" />}
                title="Name"
                subtitle={driverData?.name}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <Email color="primary" />}
                title="Email"
                subtitle={driverData?.email}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <Phone color="primary" />}
                title="Phone"
                subtitle={driverData?.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <AccountCircle color="primary" />}
                title="Driver ID"
                subtitle={driverData?.driverId}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <HealthAndSafety color="primary" />}
                title="Medical Certification Status"
                subtitle={driverData?.medicalCertificationStatus?.name}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <AccessTime color="primary" />}
                title="Created At"
                subtitle={formatDate(driverData?.createdAt)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InformationTile
                icon={() => <AccessTime color="primary" />}
                title="Updated At"
                subtitle={formatDate(driverData?.updatedAt)}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Divider between Driver Details and Assigned Vehicles */}
        <Divider sx={{ bgcolor: "grey.600", my: 3 }} />

        {/* Assigned Vehicles */}
        <Typography variant="h5" color="primary" gutterBottom>
          Assigned Vehicles
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {driverData.assigned_vehicle_ids.map((vehicle) => (
            <Card
              key={vehicle._id}
              sx={{ bgcolor: "background.paper", boxShadow: 2, width: "100%" }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InformationTile
                      icon={() => <DirectionsCar color="primary" />}
                      title="Vehicle ID"
                      subtitle={vehicle?.vehicle_id}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InformationTile
                      icon={() => <DirectionsCar color="primary" />}
                      title="Registration Number"
                      subtitle={vehicle?.registration_number}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
